import React from 'react'
import { useStableId } from './useStableId'
import { Box, OutlinedInput, TextField } from '@mui/material'
import type { TextFieldProps } from '@mui/material'
import { FormFieldShell } from './FormFieldShell'
import { buildTextFieldProps } from './buildTextFieldProps'
import { filterToolbarInlineTextFieldSx, KIOT_FORM_FIELD_CLASS, mergeSx } from './formFieldStyles'
import { resolveFieldSurface } from './FieldSurfaceContext'
import { shouldUseLabelAsPlaceholder } from './fieldSurfaces/registry'
import { isAboveLabelLayout } from './formFieldTokens'
import type { BaseFormFieldProps } from './formFieldTypes'

export type TextInputProps = BaseFormFieldProps &
  Pick<
    TextFieldProps,
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'onFocus'
    | 'onKeyDown'
    | 'inputRef'
    | 'slotProps'
    | 'InputProps'
    | 'autoComplete'
    | 'multiline'
    | 'rows'
    | 'minRows'
    | 'maxRows'
  > & {
    type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'number' | 'date' | 'datetime-local'
    style?: React.CSSProperties
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  }

export function TextInput({
  label,
  labelAction,
  placeholder,
  value = '',
  name,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  inputRef,
  required,
  disabled,
  error,
  helperText,
  size,
  fullWidth,
  sx,
  type = 'text',
  multiline,
  rows,
  minRows,
  maxRows,
  slotProps,
  InputProps,
  inputProps,
  autoComplete,
  style,
  id: idProp,
  toolbarInline = false,
  surface: surfaceProp,
}: TextInputProps) {
  const autoId = useStableId()
  const id = idProp ?? autoId
  const surface = toolbarInline ? 'filterToolbar' : resolveFieldSurface(surfaceProp)
  const labelAsPlaceholder = shouldUseLabelAsPlaceholder(surface)
  const mergedSlotProps = inputProps
    ? { ...slotProps, htmlInput: { ...slotProps?.htmlInput, ...inputProps } }
    : slotProps

  const fieldProps = buildTextFieldProps({
    label,
    placeholder,
    value,
    name,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    inputRef,
    required,
    disabled,
    error,
    helperText,
    size,
    fullWidth,
    sx,
    type,
    multiline,
    rows,
    minRows,
    maxRows,
    id,
    slotProps: mergedSlotProps,
    InputProps,
    autoComplete,
    surface,
  })

  if (toolbarInline) {
    const htmlInput =
      fieldProps.slotProps?.htmlInput && typeof fieldProps.slotProps.htmlInput === 'object'
        ? fieldProps.slotProps.htmlInput
        : undefined
    const inputSlot =
      fieldProps.slotProps?.input &&
      typeof fieldProps.slotProps.input === 'object' &&
      !('$$typeof' in fieldProps.slotProps.input)
        ? fieldProps.slotProps.input
        : undefined

    // Không dùng TextField: FormControl (DEV) tạo registerEffect mới mỗi render → InputBase setAdornedStart lặp vô hạn.
    return (
      <Box
        className={KIOT_FORM_FIELD_CLASS}
        style={style}
        sx={mergeSx(fieldProps.sx, filterToolbarInlineTextFieldSx)}
      >
        <OutlinedInput
          id={fieldProps.id}
          name={fieldProps.name}
          fullWidth
          size={fieldProps.size}
          value={value}
          placeholder={typeof fieldProps.placeholder === 'string' ? fieldProps.placeholder : undefined}
          disabled={disabled}
          error={error}
          required={required}
          autoComplete={autoComplete}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined}
          inputRef={inputRef}
          inputProps={htmlInput}
          sx={inputSlot && 'sx' in inputSlot ? inputSlot.sx : undefined}
        />
      </Box>
    )
  }

  const textField = (
    <TextField
      {...fieldProps}
      className={KIOT_FORM_FIELD_CLASS}
      style={style}
      sx={fieldProps.sx}
    />
  )

  if (isAboveLabelLayout(surface) && !labelAsPlaceholder) {
    return (
      <FormFieldShell
        label={label}
        labelAction={labelAction}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
        sx={sx}
        id={id}
        surface={surface}
      >
        {textField}
      </FormFieldShell>
    )
  }

  if (labelAsPlaceholder && (labelAction || helperText)) {
    return (
      <FormFieldShell
        label={undefined}
        labelAction={labelAction}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
        sx={sx}
        id={id}
        surface={surface}
      >
        {textField}
      </FormFieldShell>
    )
  }

  return textField
}

export default TextInput
